import classnames from 'classnames';
import type { FC } from 'react';
import { memo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Tooltip } from '../../components/GraphModal/Tooltip';
import { ReactComponent as IconMedal } from '../../icons/icon-medal-inline.svg';

export const rewardTypeTextClassnames = {
  copper: { text: 'text-bronze-400', bg: 'bg-bronze-400' },
  silver: { text: 'text-gray-350', bg: 'bg-gray-350' },
  gold: { text: 'text-yellow-250', bg: 'bg-yellow-250' },
} as const;

export type RewardsKeys = keyof typeof rewardTypeTextClassnames;
export const rewardsType = Object.keys(rewardTypeTextClassnames) as Array<RewardsKeys>;

export const intlRewards = defineMessages({
  autobiographer: {
    defaultMessage: "Complete the 'My Profile' section of the user profile",
    id: 'AGewRe',
  },
  selfier: { defaultMessage: 'Change the default avatar', id: '8EeoY2' },
  commentator: { defaultMessage: 'Leave 10 comments', id: 'JFfa/D' },
  optimistic: { defaultMessage: 'Leave 10 comments that receive 1 positive vote.', id: 'cLIsX7' },
  enthusiast: {
    defaultMessage: 'Visit the site every day for 30 consecutive days. (Days are counted in UTC.)',
    id: 'Nx4x7U',
  },
  fanatic: {
    defaultMessage: 'Visit the site every day for 100 consecutive days. (Days are counted in UTC.)',
    id: 'znPwoT',
  },
  spark: { defaultMessage: "Get a 'Brilliant' vote once on your comments", id: 'Kq2nEn' },
  epic: { defaultMessage: "Get a 'Brilliant' vote 100 times on your comments", id: '7gkZOw' },
  legendary: {
    defaultMessage: "Get a 'Brilliant' vote 1000 times on your comments",
    id: 'rOR4St',
  },
  friendly: { defaultMessage: 'Share 10 discussions on social networks', id: 'oxS8gM' },
  people: { defaultMessage: 'Share 150 discussions on social networks', id: 'IDLcT7' },
  'serial sharer': {
    defaultMessage: 'Share 1000 discussions on social networks',
    id: 'GnlKL3',
  },
  hyperlinker: {
    defaultMessage: 'Integrate content into your comments 10 times',
    id: 'uurIsa',
  },
  journalist: {
    defaultMessage: 'Integrate content into your comments 150 times',
    id: 'ihBSm1',
  },
  broadcaster: {
    defaultMessage: 'Integrate content into your comments 1000 times',
    id: 'LBZG8G',
  },
  influencer: { defaultMessage: 'Be selected once by the editorial team.', id: 'KISajT' },
  'guest star': { defaultMessage: 'Be selected 25 times by the editorial team.', id: 'lx9OQ2' },
  rasputin: { defaultMessage: 'Be selected 100 times by the editorial team.', id: 'a5RJaW' },
  firefighter: { defaultMessage: 'Report first offense', id: 'Wy2GeS' },
  citizen: { defaultMessage: 'First negative vote (off-topic or worse)', id: 'gbeBYU' },
  lobbyist: { defaultMessage: 'Reach 80 negative votes', id: '6Sz5nj' },
  detractor: { defaultMessage: 'Reach 500 negative votes', id: 'NIXCkb' },
  supporter: { defaultMessage: 'First vote (like or higher)', id: 'Xgrdur' },
  deputy: { defaultMessage: 'Reach 80 positive votes', id: 'RQnvKp' },
  marshall: { defaultMessage: 'Reach 500 positive votes', id: '0guvnJ' },
  'civic duty': { defaultMessage: 'Vote 1,000 times or more', id: 'E4v77N' },
  senator: { defaultMessage: 'Vote 30 times in a day', id: 'ruEa+n' },
  sheriff: {
    defaultMessage: 'Act as a designated moderator for at least one year',
    id: 'RMdMDf',
  },
  author: { defaultMessage: 'Create my first topic', id: 'D+ETYT' },
  bestseller: { defaultMessage: 'Create 10 topics that have 1 positive vote', id: 'tlYPR0' },
  bright: { defaultMessage: "Get 10 'Brilliant' votes on one of your topics", id: 'y1S3hS' },
  'team spirit': { defaultMessage: 'Join a group', id: 'O4RzhH' },
  'tribe chief': { defaultMessage: 'Create your own group', id: 'wAybjM' },
  leader: { defaultMessage: 'Get more than 50 members for your group', id: '5/s9wC' },
  networker: { defaultMessage: 'Invite your first friend', id: 'ZwzmYW' },
  shiny: { defaultMessage: "Get a 'Brilliant' vote 100 times on all your topics", id: 'hVvbbZ' },
  preacher: { defaultMessage: 'Get more than 250 members for your group', id: 'd9ZG4y' },
  'world star': { defaultMessage: 'Reach 500 positive votes on your topics', id: 'Watjfe' },
  celebrity: { defaultMessage: 'Get more than 1000 members for your group', id: 'S0b7DF' },
  conqueror: { defaultMessage: "Get a 'Brilliant' vote 1000 times on your topics", id: 'EhUSwe' },
  jetsetter: { defaultMessage: 'Get more than 25 FRIENDS', id: 'x/dTuX' },
});

export const rewardsDefinitions = [
  { name: 'commentator', type: 'copper' },
  { name: 'optimistic', type: 'copper' },
  { name: 'spark', type: 'copper' },
  { name: 'firefighter', type: 'copper' },
  { name: 'influencer', type: 'copper' },
  { name: 'citizen', type: 'copper' },
  { name: 'supporter', type: 'copper' },
  { name: 'selfier', type: 'copper' },
  { name: 'autobiographer', type: 'copper' },
  { name: 'author', type: 'copper' },
  { name: 'bright', type: 'copper' },
  { name: 'team spirit', type: 'copper' },
  { name: 'tribe chief', type: 'copper' },
  { name: 'leader', type: 'copper' },
  { name: 'networker', type: 'copper' },
  { name: 'friendly', type: 'copper' },
  { name: 'deputy', type: 'silver' },
  { name: 'civic duty', type: 'silver' },
  { name: 'guest star', type: 'silver' },
  { name: 'epic', type: 'silver' },
  { name: 'lobbyist', type: 'silver' },
  { name: 'bestseller', type: 'silver' },
  { name: 'shiny', type: 'silver' },
  { name: 'people', type: 'silver' },
  { name: 'preacher', type: 'silver' },
  { name: 'rasputin', type: 'gold' },
  { name: 'detractor', type: 'gold' },
  { name: 'marshall', type: 'gold' },
  { name: 'legendary', type: 'gold' },
  { name: 'world star', type: 'gold' },
  { name: 'celebrity', type: 'gold' },
  { name: 'conqueror', type: 'gold' },
  { name: 'jetsetter', type: 'gold' },
] as Array<{ name: keyof typeof intlRewards; type: RewardsKeys }>;

export type RewardProps = {
  type: keyof typeof rewardTypeTextClassnames;
  name: keyof typeof intlRewards;
};

export const RewardCard: FC<RewardProps> = memo(function RewardCard(props) {
  const { type, name } = props;
  return (
    <Tooltip content={<FormattedMessage {...intlRewards[name]} />}>
      <div
        className={classnames(
          'flex justify-center items-center px-3 py-1 truncate text-white rounded-full select-none',
          rewardTypeTextClassnames[type].bg,
        )}
      >
        <span className="opacity-75">
          <IconMedal />
        </span>
        <span className="mx-1">{name}</span>
      </div>
    </Tooltip>
  );
});
